import { useState } from "react";
import { Modal } from "../shared/Modal";
import { FormInput } from "../shared/FormInput";
import { Button } from "../shared/Button";
import { useApiCall } from "../../lib/useToastError";
import { useNotifications } from "../shared/NotificationsProvider";
import { AdminTiers, type CatalogTier } from "../../lib/apiTyped";

// Edits a tier doc. Numeric fields are kept as strings while typing so the
// user can clear them or type partial numbers without React fighting them.
// The actual conversion happens on submit, with strict NaN checks.
export function EditTierModal({
  tier,
  onClose,
  onSaved,
}: {
  tier: CatalogTier;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [price, setPrice] = useState(String(tier.priceEurPerMonth));
  const [maxSeats, setMaxSeats] = useState(String(tier.maxSeats));
  const [creditsM, setCreditsM] = useState(
    String(tier.monthlyCreditsLimit / 1_000_000),
  );
  const [storageGB, setStorageGB] = useState(
    String(tier.storageBytesLimit / 1_073_741_824),
  );
  const [rpm, setRpm] = useState(String(tier.requestsPerMinuteLimit));
  const [artifacts, setArtifacts] = useState(
    Boolean((tier.features as Record<string, unknown>).artifacts),
  );
  const [prioritySupport, setPrioritySupport] = useState(
    Boolean((tier.features as Record<string, unknown>).prioritySupport),
  );
  const [busy, setBusy] = useState(false);

  const { run } = useApiCall();
  const { confirm } = useNotifications();

  async function submit() {
    if (busy) return;
    const newPrice = Number(price);
    const newMaxSeats = Math.round(Number(maxSeats));
    const newCredits = Math.round(Number(creditsM) * 1_000_000);
    const newStorage = Math.round(Number(storageGB) * 1_073_741_824);
    const newRpm = Math.round(Number(rpm));
    if (
      [newPrice, newCredits, newStorage, newRpm].some(
        (n) => !Number.isFinite(n) || n < 0,
      ) ||
      !Number.isFinite(newMaxSeats) ||
      newMaxSeats < 1
    ) {
      // Lazy validation: the global toast layer already surfaces
      // ApiError messages on submit, but client-side numbers shouldn't
      // even hit the server. Bail with a confirm for visibility.
      await confirm({
        title: "Invalid values",
        message: "Check the numbers: max users must be >= 1, the rest >= 0.",
        confirmLabel: "OK",
        variant: "danger",
      });
      return;
    }

    const priceChanged = newPrice !== tier.priceEurPerMonth;
    if (priceChanged) {
      const ok = await confirm({
        title: "Change price",
        message:
          "A new price will be created in Stripe and all active subscriptions will be notified by email. The new rate will be charged from the next cycle (no proration).",
        confirmLabel: "Confirm change",
        variant: "danger",
      });
      if (!ok) return;
    }

    setBusy(true);
    const r = await run(
      () =>
        AdminTiers.update(tier.slug, {
          priceEurPerMonth: priceChanged ? newPrice : undefined,
          maxSeats: newMaxSeats !== tier.maxSeats ? newMaxSeats : undefined,
          monthlyCreditsLimit:
            newCredits !== tier.monthlyCreditsLimit ? newCredits : undefined,
          storageBytesLimit:
            newStorage !== tier.storageBytesLimit ? newStorage : undefined,
          requestsPerMinuteLimit:
            newRpm !== tier.requestsPerMinuteLimit ? newRpm : undefined,
          features: { artifacts, prioritySupport },
        }),
      {
        successMessage: priceChanged
          ? "Price updated and subscribers notified."
          : "Plan updated.",
      },
    );
    setBusy(false);
    if (r) onSaved();
  }

  async function deleteTier() {
    if (busy) return;
    const ok = await confirm({
      title: `Delete plan ${tier.name}?`,
      message:
        "The tier doc is removed and the Stripe Price + Product are archived. Refused if any org currently has this tier assigned — reassign or cancel them first.",
      confirmLabel: "Delete plan",
      variant: "danger",
    });
    if (!ok) return;
    setBusy(true);
    const r = await run(() => AdminTiers.delete(tier.slug), {
      successMessage: "Plan deleted.",
    });
    setBusy(false);
    if (r) onSaved();
  }

  return (
    <Modal onClose={onClose} maxWidth="32rem">
      <h2 className="text-xl font-black mb-1 pr-6 text-ink">
        Edit plan {tier.name}
      </h2>
      <p className="text-sm text-ink-3 mb-5">
        The name cannot be edited. Changing the price creates a new price in
        Stripe and migrates active subscriptions for the next cycle. Credits,
        storage and max users are TOTALS for the org. Saving propagates new
        limits to every active org pool on this tier.
      </p>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            label="Price (€ / month)"
            value={price}
            onChange={setPrice}
            type="number"
          />
          <FormInput
            label="Max users"
            value={maxSeats}
            onChange={setMaxSeats}
            type="number"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            label="Credits / month TOTAL (millions)"
            value={creditsM}
            onChange={setCreditsM}
            type="number"
          />
          <FormInput
            label="Storage TOTAL (GB)"
            value={storageGB}
            onChange={setStorageGB}
            type="number"
          />
        </div>
        <FormInput
          label="Requests / minute (per member)"
          value={rpm}
          onChange={setRpm}
          type="number"
        />

        <div className="space-y-2 pt-2">
          <ToggleRow
            label="Artifacts"
            checked={artifacts}
            onChange={setArtifacts}
          />
          <ToggleRow
            label="Priority support"
            checked={prioritySupport}
            onChange={setPrioritySupport}
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <Button
          variant="primary"
          onClick={submit}
          disabled={busy}
        >
          {busy ? "Saving…" : "Save changes"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <div className="ml-auto">
          <Button variant="danger" size="sm" onClick={deleteTier} disabled={busy}>
            Delete plan
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm text-ink-2">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-ink"
      />
    </label>
  );
}
